import { AxiosError, AxiosResponse } from 'axios';
import { useAppDispatch } from 'hooks/useAppDispatch';
import { FC, ReactElement, useEffect, useState } from 'react';
import { useMutation, useQueryClient } from 'react-query';
import { useSelector } from 'react-redux';
import { modalActions } from 'reduxStore/reducers/ModalSlice';
import { getModalState } from 'reduxStore/reducers/ModalSlice/selectors/getModaState';
import { AgentService } from 'services/AgentService';
import { PaymentRequestBudgetDto } from 'services/AgentService/types';
import { BaseError } from 'types';
import { Button } from 'ui/redesign/Button';
import { ButtonVariant } from 'ui/redesign/Button/constants';
import { Spinner } from 'ui/Spinner';
import { alertBaseError, formatAmount } from 'utils/helpers';
import { ROUBLE_SIGN } from 'utils/settings';
import { ResultForm } from '../ResultForm';
import { PayFormProps } from './types';

export const PayForm: FC<PayFormProps> = ({ order }) => {
  const dispatch = useAppDispatch();
  const { options } = useSelector(getModalState);
  const queryClient = useQueryClient();

  const requestDoneHandler = (title: string, content: ReactElement) => {
    content &&
      options &&
      dispatch(
        modalActions.handleOpenModal({
          content,
          options: {
            ...options,
            title,
          },
        })
      );
  };

  const {
    mutateAsync: getBudgetByIds,
    data: budgetByIds,
    isLoading: isBudgetByIdsLoading,
  } = useMutation<AxiosResponse<PaymentRequestBudgetDto>, AxiosError<BaseError>, { ids: string[] }>(
    {
      mutationKey: 'getBudgetByIds',
      mutationFn: (body) => AgentService.getBudgetByIds(body),
      onError: (err) => {
        alertBaseError(err);
      },
    }
  );

  const budget = budgetByIds?.data;

  const { mutateAsync: payingRequest, isLoading: isPaymentRequestLoading } = useMutation<
    AxiosResponse,
    AxiosError<BaseError>,
    { orderId: string; concurrencyStamp: string }
  >({
    mutationKey: 'payingRequest',
    mutationFn: (payload: { orderId: string; concurrencyStamp: string }) =>
      AgentService.paying(payload.orderId, payload.concurrencyStamp),
    onSuccess: () => {
      requestDoneHandler(
        'Проверки пройдены успешно. Начался процесс выплат...',
        <div className="mt-6 flex justify-center">
          <Button className="paragraphs-bold mb-1 w-[190px]" onClick={closeModal}>
            ПРИНЯТЬ
          </Button>
        </div>
      );
      queryClient.refetchQueries('getClientOrder');
    },
    onError: (data) => {
      requestDoneHandler(
        data.response?.data.detail ?? 'Произошла ошибка',
        <div className="mt-6 flex justify-center">
          <Button
            className="paragraphs-bold mb-1 w-[190px]"
            onClick={closeModal}
            variant={ButtonVariant.OUTLINE}
          >
            ЗАКРЫТЬ
          </Button>
        </div>
      );
    },
  });

  const closeModal = () => {
    dispatch(modalActions.closeModal());
  };

  useEffect(() => {
    getBudgetByIds({ ids: [order.id] });
  }, []);

  return (
    <div className="w-full p-4 sm:min-w-[500px] sm:p-0">
      <div className="mt-6 flex justify-center gap-6">
        {isPaymentRequestLoading || isBudgetByIdsLoading ? (
          <Spinner />
        ) : (
          <div className="flex w-full flex-col gap-6">
            <div>
              <p className="headline-normal">
                Общая сумма к выплате с учётом комиссии:{' '}
                <span className="headline-bold">
                  {formatAmount(budget?.sumForPaymentWithNBCOFee || 0, {
                    minimumFractionDigits: 2,
                  })}
                  &nbsp;{ROUBLE_SIGN}
                </span>
              </p>
              <p className="headline-normal">
                Сумма к начислению самозанятым:{' '}
                <span className="headline-bold">
                  {formatAmount(budget?.sumForAgent || 0, {
                    minimumFractionDigits: 2,
                  })}
                  &nbsp;{ROUBLE_SIGN}
                </span>
              </p>
              <p className="headline-normal">
                Сумма комиссии:{' '}
                <span className="headline-bold">
                  {formatAmount(budget?.sumFees || 0, {
                    minimumFractionDigits: 2,
                  })}
                  &nbsp;{ROUBLE_SIGN}
                </span>
              </p>
            </div>
            <div className="flex gap-6">
              <Button
                variant={ButtonVariant.GHOST}
                onClick={closeModal}
                className="paragraphs-bold mb-1 ml-[auto] w-[130px] border border-solid border-text-50 !text-text-100"
              >
                ОТМЕНА
              </Button>
              <Button
                className="paragraphs-bold mb-1 w-[190px]"
                onClick={() => {
                  if (order.concurrencyStamp) {
                    payingRequest({
                      orderId: order.id,
                      concurrencyStamp: order.concurrencyStamp,
                    }).catch((err) => err);
                    dispatch(
                      modalActions.handleOpenModal({
                        content: <ResultForm />,
                        options: {
                          ...options,
                          title: 'Осуществляется проверка',
                          contentClassName: 'overflow-hidden flex mt-6',
                        },
                      })
                    );
                  }
                }}
              >
                ОПЛАТИТЬ
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
